// Imported from the file, not the `integrations` barrel: the barrel and this
// one are both re-exported from the package root, and going through it would
// make the two domains circular at module-init time.
import { type IntegrationCapability } from '../../../integrations/integration-capability/integration-capability';
import { INTEGRATION_MANAGE_PERMISSIONS } from '../../../integrations/integration-catalog/integration-catalog';

import {
  GLOBAL_MANAGE,
  MANAGE_ACTION,
  permissionAction,
  permissionResource,
  type PermissionKey,
} from '../permission-key/permission-key';

/**
 * One declared permission. Labels are deliberately absent: they are translated
 * content and live in the admin i18n catalogs (U8), keyed by the permission
 * key — a label here would be an untranslatable string in a shared library.
 */
export interface PermissionDefinition {
  key: PermissionKey;
  /** The namespace the key belongs to — the admin UI groups its checklist by this. */
  resource: string;
  action: string;
}

/**
 * The **code-declared permission catalog** (N8/N22) — the only place a
 * permission key comes into existence. The database stores rows seeded from
 * this list; a key that is not here does not exist, and a required key that is
 * not here resolves to **deny** (fail-closed).
 *
 * Keys removed from this list are **deactivated** at the next boot seed, never
 * hard-deleted: audit rows and historical grants must keep resolving to a
 * readable key.
 *
 * `<resource>.manage` grants every action on its namespace, and the global
 * `manage` grants everything — so a resource does not need a `manage` sibling
 * for each action, only where staff should be able to hold the whole resource.
 *
 * Later phases extend this: P4 derives `integrations.<capability>.manage` from
 * the integrations capability catalog (N21). Those derived keys **must be
 * appended to this list in code**, not only to the database seed — the guard
 * and the admin role editor both read the code catalog, so a key that exists
 * only as a row would be grantable, invisible in the editor, and denied by the
 * guard (unknown key = deny). Both catalogs are code-declared, so deriving one
 * from the other here is a pure function, not a runtime lookup.
 */
const STATIC_KEYS = [
  // Global wildcard — held by `super_admin` only.
  GLOBAL_MANAGE,

  // Staff access control itself (N22) — the narrowest, most dangerous surface.
  'access.read',
  'access.manage',

  'dashboard.read',

  'members.read',
  'members.manage',

  'verification.review',
  'verification.manage',

  'trust.review',
  'trust.manage',

  // The question builder (P6). Split read/manage like `settings`: a staff member
  // may need to see what members are asked — to answer a support question, or to
  // read a visibility policy — without holding the power to change what every
  // member is asked and who can see their answers.
  'questions.read',
  'questions.manage',

  'settings.read',
  'settings.manage',

  'integrations.read',
  'integrations.manage',

  'media.review',

  // Brand assets (P10.2e) — the platform-owned media a `media`-controlled
  // setting points at, e.g. `brand.logoMediaId`.
  //
  // Deliberately its **own resource** rather than a `media.manage` sibling of
  // `media.review`: `media.manage` would be a wildcard over `media.review`, so
  // granting it would silently hand over every member's profile photos, and —
  // because a deny matches in both directions — denying `media.review` on a
  // brand-manager role would deny the manage key with it. "Manage the brand
  // logo, never read members' photos" has to remain expressible.
  'brand.read',
  'brand.manage',

  'billing.read',
  'billing.manage',

  // The curriculum (P22). Split read/manage like `questions` and `settings`, and
  // for a sharper reason than either: `courses.manage` authors what **grants
  // money**, while the attempts viewer and the grants viewer are where a support
  // question ("why has this member not got their discount?") is answered. Those
  // two jobs should not need the same key.
  'courses.read',
  'courses.manage',

  'legal.read',
  'legal.manage',

  'notifications.read',
  'notifications.manage',

  'moderation.review',
  'moderation.manage',

  'violations.review',
  'violations.manage',

  'meetings.read',
  'meetings.manage',

  'ai.read',
  'ai.manage',

  'audit.read',
] as const satisfies readonly PermissionKey[];

/**
 * The per-capability integration keys (N21), **derived** from the integrations
 * catalog rather than written out here.
 *
 * Both catalogs are code-declared, so this is a pure computation — and it is
 * what makes `integrations.storage.manage` real everywhere at once: the boot
 * seed materializes it, `PermissionsGuard` accepts it (an undeclared key is
 * denied), and the admin role editor lists it. Adding a capability adds its
 * permission with no edit here.
 */
const DERIVED_INTEGRATION_KEYS: readonly `integrations.${IntegrationCapability}.manage`[] =
  INTEGRATION_MANAGE_PERMISSIONS;

const DECLARED_KEYS: readonly PermissionKey[] = [...STATIC_KEYS, ...DERIVED_INTEGRATION_KEYS];

/** A key the catalog declares — usable as the argument type of `@RequirePermissions`. */
export type CatalogPermissionKey =
  (typeof STATIC_KEYS)[number] | `integrations.${IntegrationCapability}.manage`;

export const PERMISSION_CATALOG: readonly PermissionDefinition[] = DECLARED_KEYS.map((key) => ({
  key,
  resource: permissionResource(key),
  action: permissionAction(key),
}));

const CATALOG_KEYS: ReadonlySet<string> = new Set<string>(DECLARED_KEYS);

/**
 * Whether a key exists in the catalog. Used by the boot seed, by the guard's
 * fail-closed check, and by the admin UI to reject a stale checkbox.
 *
 * `known` is injectable so a phase that derives keys at runtime (P4's
 * `integrations.<capability>.manage`) can widen the set without a second
 * evaluator existing anywhere.
 */
export function isKnownPermissionKey(
  key: string,
  known: ReadonlySet<string> = CATALOG_KEYS,
): boolean {
  return known.has(key);
}

/** The declared keys as a set — the default `known` argument everywhere. */
export function catalogKeySet(): ReadonlySet<string> {
  return CATALOG_KEYS;
}

/** The catalog grouped by resource — the shape the admin role editor renders (P10). */
export function permissionsByResource(): ReadonlyMap<string, readonly PermissionDefinition[]> {
  const grouped = new Map<string, PermissionDefinition[]>();

  for (const definition of PERMISSION_CATALOG) {
    const bucket = grouped.get(definition.resource);
    if (bucket === undefined) {
      grouped.set(definition.resource, [definition]);
    } else {
      bucket.push(definition);
    }
  }

  return grouped;
}

/** Whether a declared key is the global wildcard — kept here so callers need no literal. */
export function isGlobalManage(key: string): boolean {
  return key === MANAGE_ACTION;
}
